import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import { RootState } from '../../store'
import { OpportunityDetailPresenter } from './opportunity-detail-presenter'
import {
  CMSOpportunityDetailsType,
  Investment,
  OpportunityAllocationInterest,
  OpportunityDetails,
  UserInvestmentDetails,
} from '../../lib/types'
import {
  projectServices,
  investServices,
  kycServices,
  sanityServices,
  opportunityServices,
} from '../../services'
import { whatsAppService } from '../../services/whatsapp-support'
import { showBanner } from '../../global-state/banner-slice'
import { calculateRemainingDays, displayDollar } from '../../lib/utils/helpers'
import { Anchor } from '../../lib/components'
import { updateConfiguration } from '../../global-state/investor-slice'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import { EngagementEvents, logCrmEvent, updateCrm } from '../../lib/utils/crmEvents'
import {
  INDICATE_INTEREST_WHATSAPP_ERROR,
  INDICATE_INTEREST_WHATSAPP_PHONE_REQUIRED,
  INDICATE_INTEREST_WHATSAPP_SUCCESS,
  PROCEED_WITH_INVESTMENT,
  REQUEST_ALLOCATION,
  UPDATE_ALLOCATION_REQUEST,
  ALLOCATION_REQUEST_SUBMIT_ERROR,
  ALLOCATION_REQUEST_NOT_ENABLED_ERROR,
  ALLOCATION_REQUEST_DELETE_SUCCESS,
  ALLOCATION_REQUEST_DELETE_ERROR,
  COMPLETE_THE_KYC,
  COMPLETE_KYC_TO_INVEST_MESSAGE,
  FINISH_SETTING_UP_PROFILE,
  FINISH_SETTING_UP_PROFILE_MESSAGE,
  FINISH_SETTING_UP_PROFILE_PATH,
} from '../../lib/utils/message'
import { resolveRiskProfilerRequired } from '../../lib/utils/onboarding'
import ExtendLimitModal from '../../lib/components/ExtendLimitModal/extend-limit-modal'
import { usePendingInvestmentLimitRequest } from '../../lib/utils/use-pending-investment-limit-request'
import { storeOpportunityComplianceReturn } from '../../lib/utils/compliance-return-redirect'
import RequestAllocationModal, { RequestAllocationModalStep } from './request-allocation-modal'

const OpportunityDetailContainer = () => {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const kycPollIntervalRef = useRef<number | null>(null)
  const { id, projectSlug } = params
  const accessToken = searchParams.get('access')
  const { authToken, kycStatus, userId, riskProfileCompleted, riskProfilerRequired } = useSelector(
    (state: RootState) => state.investor,
  )
  const isRiskProfilerPending = resolveRiskProfilerRequired({
    riskProfilerRequired,
    riskProfileCompleted,
  })
  const [opportunity, setOpportunity] = useState<OpportunityDetails>()
  const [isSaveForLater] = useState(false)
  const [userInvestmentDetail, setUserInvestmentDetail] = useState<UserInvestmentDetails>()
  const [userInvestmentLatest, setUserInvestmentLatest] = useState<Investment>({} as Investment)
  const [showContactusModal, setContactUsModal] = useState(false)
  const [showExtendLimitModal, setShowExtendLimitModal] = useState(false)
  const { hasPendingRequest, markPending, openIfAllowed } = usePendingInvestmentLimitRequest()
  const [investButtonProperty, setInvestButtonProperty] = useState({
    buttonText: PROCEED_WITH_INVESTMENT,
    isDisable: false,
    kycStatus: kycStatus || 'PENDING',
    investmentInfo: <></>,
  })
  const [opportunityDetails, setOpportunityDetails] = useState<CMSOpportunityDetailsType>({})
  const [clientAgreementSigned, setClientAgreementSigned] = useState<boolean>(false)
  const [isIndicatingInterest, setIsIndicatingInterest] = useState(false)
  const [allocationInterest, setAllocationInterest] =
    useState<OpportunityAllocationInterest | null>(null)
  const [showRequestAllocationModal, setShowRequestAllocationModal] = useState(false)
  const [requestAllocationStep, setRequestAllocationStep] =
    useState<RequestAllocationModalStep>('form')
  const [isSubmittingAllocation, setIsSubmittingAllocation] = useState(false)
  const [isDeletingAllocation, setIsDeletingAllocation] = useState(false)

  const isRequestAllocationEnabled = opportunity?.metadata?.request_allocation_enabled === true

  const getProceedCtaText = () => {
    if (!isRequestAllocationEnabled) {
      return PROCEED_WITH_INVESTMENT
    }
    return allocationInterest ? UPDATE_ALLOCATION_REQUEST : REQUEST_ALLOCATION
  }

  const closeRequestAllocationModal = () => {
    setShowRequestAllocationModal(false)
    setRequestAllocationStep('form')
    setIsSubmittingAllocation(false)
    setIsDeletingAllocation(false)
  }

  const fetchAllocationInterest = async (opportunityId: number) => {
    if (!authToken) {
      setAllocationInterest(null)
      return
    }
    try {
      const response = await opportunityServices.getOpportunityAllocationInterest(opportunityId, {
        Authorization: authToken,
      })
      if (response?.status === 200) {
        setAllocationInterest(response.data ?? null)
      }
    } catch {
      setAllocationInterest(null)
    }
  }

  const handleRequestAllocationSubmit = async (amountCents: number) => {
    if (!opportunity?.id || isSubmittingAllocation || isDeletingAllocation) {
      return
    }

    setIsSubmittingAllocation(true)
    try {
      const response = await opportunityServices.submitOpportunityAllocationInterest(
        opportunity.id,
        amountCents,
        { Authorization: authToken ? authToken : '' },
      )

      if (response?.status === 200 || response?.status === 201) {
        setAllocationInterest(
          response.data ?? ({ amount: amountCents } as OpportunityAllocationInterest),
        )
        setRequestAllocationStep('success')
        await fetchAllocationInterest(opportunity.id)
        return
      }

      const errorCode = response?.data?.code
      dispatch(
        showBanner({
          text:
            errorCode === 'request-allocation-not-enabled'
              ? ALLOCATION_REQUEST_NOT_ENABLED_ERROR
              : response?.data?.message || ALLOCATION_REQUEST_SUBMIT_ERROR,
          variant: 'error-banner',
        }),
      )
    } catch {
      dispatch(
        showBanner({
          text: ALLOCATION_REQUEST_SUBMIT_ERROR,
          variant: 'error-banner',
        }),
      )
    } finally {
      setIsSubmittingAllocation(false)
    }
  }

  const handleRequestAllocationDelete = async () => {
    if (!opportunity?.id || isSubmittingAllocation || isDeletingAllocation) {
      return
    }

    setIsDeletingAllocation(true)
    try {
      const response = await opportunityServices.deleteOpportunityAllocationInterest(
        opportunity.id,
        { Authorization: authToken ? authToken : '' },
      )

      if (response?.status === 200 || response?.status === 204) {
        setAllocationInterest(null)
        closeRequestAllocationModal()
        dispatch(
          showBanner({
            text: ALLOCATION_REQUEST_DELETE_SUCCESS,
            variant: 'success-banner',
          }),
        )
        return
      }

      dispatch(
        showBanner({
          text: response?.data?.message || ALLOCATION_REQUEST_DELETE_ERROR,
          variant: 'error-banner',
        }),
      )
    } catch {
      dispatch(
        showBanner({
          text: ALLOCATION_REQUEST_DELETE_ERROR,
          variant: 'error-banner',
        }),
      )
    } finally {
      setIsDeletingAllocation(false)
    }
  }

  const indicateInterestHandleClick = async () => {
    if (!opportunity?.id || isIndicatingInterest) {
      return
    }

    setIsIndicatingInterest(true)
    try {
      const response = await whatsAppService.requestOpportunityWhatsApp(opportunity.id)

      if (response?.status === 200) {
        dispatch(
          showBanner({
            text: INDICATE_INTEREST_WHATSAPP_SUCCESS,
            variant: 'success-banner',
          }),
        )
      } else if (response?.status === 400) {
        dispatch(
          showBanner({
            text: INDICATE_INTEREST_WHATSAPP_PHONE_REQUIRED,
            variant: 'error-banner',
          }),
        )
      } else {
        dispatch(
          showBanner({
            text: INDICATE_INTEREST_WHATSAPP_ERROR,
            variant: 'error-banner',
          }),
        )
      }
    } catch {
      dispatch(
        showBanner({
          text: INDICATE_INTEREST_WHATSAPP_ERROR,
          variant: 'error-banner',
        }),
      )
    } finally {
      setIsIndicatingInterest(false)
    }
  }

  const navigateToKycWithReturn = (returnSlug: string, returnId: number) => {
    storeOpportunityComplianceReturn(returnSlug, returnId, accessToken)
    navigate('/profile/kyc')
  }

  const investHandleClick = (projectSlug: string, id: number) => {
    if (isRiskProfilerPending) {
      navigate(FINISH_SETTING_UP_PROFILE_PATH)
      return
    }

    if (kycStatus) {
      // KYC-pending clients may express interest via Request Allocation (no investment/order created).
      if (kycStatus === 'PENDING' && isRequestAllocationEnabled) {
        setRequestAllocationStep('form')
        setShowRequestAllocationModal(true)
        return
      }

      if (!clientAgreementSigned) {
        dispatch(
          showBanner({
            text: 'Please complete the Client Agreement & Self-Declaration signing to proceed with investment.',
            variant: 'error-banner',
          }),
        )
        navigateToKycWithReturn(projectSlug, id)
        return
      }
      if (kycStatus !== 'APPROVED') {
        navigateToKycWithReturn(projectSlug, id)
        return
      } else {
        if (userInvestmentLatest) {
          if (userInvestmentLatest.status === 'ORDER_PLACED') {
            navigate('/dashboard')
            return
          }

          // Request Allocation stays available for limit/raise-goal cases; only KYC gates disable the CTA
          if (isRequestAllocationEnabled && userInvestmentLatest.status !== 'INITIATED') {
            setRequestAllocationStep('form')
            setShowRequestAllocationModal(true)
            return
          }

          if (
            userInvestmentLatest.status === 'INITIATED' ||
            (opportunity &&
              opportunity?.ticketSize &&
              userInvestmentDetail &&
              userInvestmentDetail.canInvest &&
              parseInt(opportunity?.ticketSize) <= parseInt(userInvestmentDetail.canInvest))
          ) {
            if (isRequestAllocationEnabled && userInvestmentLatest.status !== 'INITIATED') {
              setRequestAllocationStep('form')
              setShowRequestAllocationModal(true)
              return
            }

            logCrmEvent(
              EngagementEvents.CLICKED_INVEST_BUTTON,
              {
                Authorization: authToken ?? '',
              },
              {
                opportunityName: opportunity?.projectName,
                opportunityId: opportunity?.id,
                investmentOrderid: userInvestmentLatest.order_id,
              },
            )

            if (accessToken) {
              navigate(`/invest-v2/link/${projectSlug}?access=${accessToken}`)
            } else {
              navigate(`/invest-v2/${projectSlug}/${id}`)
            }
            captureEvent(EVENTS.investment_started, {
              projectSlug,
              id,
              userInvestmentDetail,
              project: opportunity,
            }) // Capture the event here
          } else {
            void openIfAllowed(() => setShowExtendLimitModal(true))
          }
        }
      }
    }
  }

  const backToProjectHandler = () => navigate('/opportunities')

  const handleClickModal = () => {
    setContactUsModal((state) => !state)
  }

  useEffect(() => {
    const getUserInvestments = async () => {
      const response = await investServices.getUserInvestmentDetail_v1({
        Authorization: authToken ? authToken : '',
      })
      if (response && response.data && response.status === 200) {
        setUserInvestmentDetail(response.data)
      }
    }
    const getUserInvestmentLatest = async () => {
      if (!opportunity?.id) return

      const response = await investServices.getInvestmentLatest_v1(
        { opportunityId: opportunity.id },
        {
          Authorization: authToken ? authToken : '',
        },
      )
      if (response && response.data && response.status === 200) {
        setUserInvestmentLatest(response.data)
      }
    }
    const getKYC = async () => {
      const response = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })
      if (!!response && response.data && response.status === 200) {
        if (response.data.status) {
          dispatch(
            updateConfiguration({
              name: 'kycStatus',
              value: response.data.status,
            }),
          )
        }
        // Set client agreement status
        setClientAgreementSigned(!!response.data.client_agreement_signed)
      }
    }
    if (authToken) {
      getKYC()
      getUserInvestments()
      getUserInvestmentLatest()
    }
  }, [authToken, userId, opportunity?.id])
  useEffect(() => {
    const handleInvestButtonProperty = () => {
      if (
        opportunity?.closingDate &&
        calculateRemainingDays(new Date(), new Date(opportunity?.closingDate)) <= 0
      ) {
        setInvestButtonProperty({
          ...investButtonProperty,
          buttonText: 'Closed for investment',
          isDisable: true,
          investmentInfo: (
            <>
              This opportunity has been closed for investment. You can not invest in this
              opportunity. Please contact the{' '}
              <span onClick={() => setContactUsModal(true)}>
                &nbsp;
                {
                  <Anchor
                    variant='basic-anchor'
                    href=''
                    text='Credibila Team'
                    dataTestId='opportunity-closed-for-investment'
                  />
                }
                &nbsp;
              </span>{' '}
              for more information.
            </>
          ),
        })
      }
      if (
        opportunity?.closingDate &&
        calculateRemainingDays(new Date(), new Date(opportunity?.closingDate)) > 0
      ) {
        setInvestButtonProperty({
          ...investButtonProperty,
          buttonText: getProceedCtaText(),
          isDisable: false,
          investmentInfo: <></>,
        })
        if (
          kycStatus &&
          (kycStatus === 'NOT_STARTED' ||
            kycStatus === 'INITIATED' ||
            kycStatus === 'RESUBMIT' ||
            kycStatus === 'EXPIRED')
        ) {
          setInvestButtonProperty({
            ...investButtonProperty,
            buttonText: COMPLETE_THE_KYC,
            investmentInfo: <>{COMPLETE_KYC_TO_INVEST_MESSAGE}</>,
          })
        }
        if (kycStatus && kycStatus === 'PENDING') {
          setInvestButtonProperty({
            ...investButtonProperty,
            // EOI allowed while KYC awaits approval when Request Allocation is enabled
            isDisable: !isRequestAllocationEnabled,
            buttonText: getProceedCtaText(),
            investmentInfo: (
              <>
                The KYC is awaiting Credibila team approval. KYC approval is mandatory for investing
                in the opportunities. Go to the&nbsp;
                {<Anchor variant='basic-anchor' href='/profile/kyc' text='KYC' />} &nbsp; section.
              </>
            ),
          })
        }
        if (kycStatus && kycStatus === 'REJECTED') {
          setInvestButtonProperty({
            ...investButtonProperty,
            isDisable: true,
            buttonText: getProceedCtaText(),
            investmentInfo: (
              <>
                Your KYC was rejected, please go to the&nbsp;
                {<Anchor variant='basic-anchor' href='/profile/kyc' text='KYC' />}
                &nbsp; section for more details.
              </>
            ),
          })
        }
        // Client agreement takes priority over incomplete KYC messaging when unsigned.
        // Do not override EOI CTA for PENDING + request allocation (interest does not require agreement).
        if (
          kycStatus &&
          !clientAgreementSigned &&
          !(kycStatus === 'PENDING' && isRequestAllocationEnabled)
        ) {
          setInvestButtonProperty({
            ...investButtonProperty,
            isDisable: false,
            buttonText: 'Complete Client Agreement & Self-Declaration',
            investmentInfo: (
              <>
                Please complete the Client Agreement & Self-Declaration signing to proceed with
                investment. Go to the &nbsp;
                {<Anchor variant='basic-anchor' href='/profile/kyc' text='KYC' />} &nbsp; section.
              </>
            ),
          })
        }
        if (isRiskProfilerPending) {
          setInvestButtonProperty({
            ...investButtonProperty,
            isDisable: false,
            buttonText: FINISH_SETTING_UP_PROFILE,
            investmentInfo: (
              <>
                {FINISH_SETTING_UP_PROFILE_MESSAGE} Go to the&nbsp;
                {
                  <Anchor
                    variant='basic-anchor'
                    href={FINISH_SETTING_UP_PROFILE_PATH}
                    text='profile setup'
                  />
                }
                &nbsp; section.
              </>
            ),
          })
        }
        if (kycStatus && kycStatus === 'APPROVED' && clientAgreementSigned) {
          if (userInvestmentDetail && parseInt(userInvestmentDetail.investmentLimit) === 0) {
            setInvestButtonProperty({
              ...investButtonProperty,
              // Keep Request Allocation clickable; only non-KYC states disable the CTA
              isDisable: !isRequestAllocationEnabled,
              buttonText: getProceedCtaText(),
              investmentInfo: (
                <>
                  You are not yet assigned an investment amount limit by the Credibila Team to
                  invest in the opportunities. The investment limit is assigned based on your
                  information collected during KYC. Please contact the{' '}
                  <span onClick={() => setContactUsModal(true)}>
                    &nbsp;
                    {
                      <Anchor
                        variant='basic-anchor'
                        href=''
                        text='Credibila Team'
                        dataTestId='investor-investment-limit-not-assigned'
                      />
                    }
                    &nbsp;
                  </span>{' '}
                  for more information.
                </>
              ),
            })
          } else if (
            userInvestmentLatest &&
            userInvestmentLatest.status !== 'INITIATED' &&
            opportunity &&
            opportunity.ticketSize &&
            userInvestmentDetail
          ) {
            const ticketSizeAmount = parseInt(opportunity.ticketSize)
            const availableLimitAmount = parseInt(userInvestmentDetail.canInvest)
            // Opportunities without a raise goal skip the raise-goal check but still enforce limits
            const hasRaiseGoal = !!opportunity.raiseGoal && opportunity.raiseGoal !== '0'
            const remainingRaiseGoalAmount = hasRaiseGoal
              ? parseInt(opportunity.raiseGoal) - parseInt(opportunity.committedAmount || '0')
              : null

            if (remainingRaiseGoalAmount !== null && remainingRaiseGoalAmount < ticketSizeAmount) {
              setInvestButtonProperty({
                ...investButtonProperty,
                isDisable: !isRequestAllocationEnabled,
                buttonText: getProceedCtaText(),
                investmentInfo: (
                  <>
                    The opportunity is about to reach it&apos;s raise goal value of{' '}
                    {displayDollar(opportunity?.raiseGoal, opportunity?.currencyCode) || ''} and
                    will be closing soon. Please contact the{' '}
                    <span onClick={() => setContactUsModal(true)}>
                      &nbsp;
                      {
                        <Anchor
                          variant='basic-anchor'
                          href=''
                          text='Credibila Team'
                          dataTestId='opportunity-nearing-raise-goal'
                        />
                      }
                      &nbsp;
                    </span>{' '}
                    in case you still want to invest in this opportunity.
                  </>
                ),
              })
            } else if (
              Number.isFinite(availableLimitAmount) &&
              ticketSizeAmount > availableLimitAmount
            ) {
              setInvestButtonProperty({
                ...investButtonProperty,
                isDisable: isRequestAllocationEnabled ? false : hasPendingRequest,
                buttonText: isRequestAllocationEnabled
                  ? getProceedCtaText()
                  : hasPendingRequest
                  ? 'Request under review'
                  : 'Place a request to increase the Investment Limit',
                investmentInfo: (
                  <>
                    The amount you wish to invest exceeds your current available investment limit of{' '}
                    {displayDollar(
                      userInvestmentDetail.canInvest,
                      opportunity?.currencyCode,
                      2,
                      true,
                    )}
                    .{' '}
                    {hasPendingRequest ? (
                      <span>Your request to increase the Investment Limit is under review.</span>
                    ) : (
                      <>
                        <span
                          onClick={() => void openIfAllowed(() => setShowExtendLimitModal(true))}
                        >
                          {
                            <Anchor
                              variant='basic-anchor'
                              href=''
                              text='Place a request to increase the Investment Limit'
                              dataTestId='investor-investment-limit-reached'
                            />
                          }
                        </span>
                        .
                      </>
                    )}
                  </>
                ),
              })
            }
          }
          if (userInvestmentLatest && userInvestmentLatest.status === 'INITIATED') {
            setInvestButtonProperty({
              ...investButtonProperty,
              isDisable: false,
              buttonText: 'Ongoing Investment',
              investmentInfo: (
                <>
                  Complete the ongoing Investment. Please contact the{' '}
                  <span onClick={() => setContactUsModal(true)}>
                    &nbsp;
                    {
                      <Anchor
                        variant='basic-anchor'
                        href=''
                        text='Credibila Team'
                        dataTestId='investor-ongoing-investment'
                      />
                    }
                    &nbsp;
                  </span>{' '}
                  for more information.
                </>
              ),
            })
          }
        }
      }
      if (userInvestmentLatest && userInvestmentLatest.status === 'ORDER_PLACED') {
        setInvestButtonProperty({
          ...investButtonProperty,
          isDisable: false,
          buttonText: 'Ongoing Investment',
          investmentInfo: (
            <>
              You have an investment that is currently being processed by the Credibila Team for
              this opportunity. You can raise another investment request once the funds have been
              allocated to you or investment request is closed by the Credibila Team. Please contact
              the{' '}
              <span onClick={() => setContactUsModal(true)}>
                &nbsp;
                {
                  <Anchor
                    variant='basic-anchor'
                    href=''
                    text='Credibila Team'
                    dataTestId='investor-order-placed-investment'
                  />
                }
                &nbsp;
              </span>{' '}
              for more information.
            </>
          ),
        })
      }
    }
    handleInvestButtonProperty()
  }, [
    opportunity,
    userInvestmentLatest,
    userInvestmentDetail,
    kycStatus,
    clientAgreementSigned,
    allocationInterest,
    isRequestAllocationEnabled,
    hasPendingRequest,
    isRiskProfilerPending,
  ])

  useEffect(() => {
    if (!authToken || !opportunity?.id || !isRequestAllocationEnabled) {
      setAllocationInterest(null)
      return
    }
    fetchAllocationInterest(opportunity.id)
  }, [authToken, opportunity?.id, isRequestAllocationEnabled])

  // Poll client agreement status when user is logged in and agreement not signed
  useEffect(() => {
    // Clear any existing interval first
    if (kycPollIntervalRef.current) {
      clearInterval(kycPollIntervalRef.current)
      kycPollIntervalRef.current = null
    }

    // Only start polling if conditions are met
    if (!authToken || !userId || clientAgreementSigned) {
      return
    }

    // Start polling
    kycPollIntervalRef.current = window.setInterval(async () => {
      try {
        const response = await kycServices.getKYC_v1(userId.toString(), {
          Authorization: authToken,
        })

        if (response?.data?.client_agreement_signed) {
          setClientAgreementSigned(true)
          // Stop polling once agreement is signed
          if (kycPollIntervalRef.current) {
            clearInterval(kycPollIntervalRef.current)
            kycPollIntervalRef.current = null
          }
        }
      } catch (error) {
        console.error('Error polling client agreement status:', error)
      }
    }, 3000) // Poll every 3 seconds

    // Cleanup on unmount or when dependencies change
    return () => {
      if (kycPollIntervalRef.current) {
        clearInterval(kycPollIntervalRef.current)
        kycPollIntervalRef.current = null
      }
    }
  }, [authToken, userId, clientAgreementSigned])

  useEffect(() => {
    const fetchProjectById = async () => {
      let data

      if (accessToken && projectSlug) {
        // For link-only opportunities, ensure user is authenticated
        if (!authToken) {
          navigate('/opportunities')
          return
        }
        data = await projectServices.getOpportunityBySlugAndToken(
          projectSlug as string,
          accessToken,
        )
      } else {
        data = await projectServices.getOpportunityById(id as string, {
          Authorization: authToken ? authToken : '',
        })
      }

      if (data && data.data && data.status === 200) {
        const opportunity = data.data
        setOpportunity(opportunity)
        if (authToken) {
          updateCrm('last_opportunity_view_date', Date.now().toString(), {
            Authorization: authToken ?? '',
          })
          logCrmEvent(
            EngagementEvents.VIEWED_OPPORTUNITY_DETAIL_PAGE,
            {
              Authorization: authToken ?? '',
            },
            { opportunityName: opportunity?.projectName, opportunityId: opportunity?.id },
          )
        }
      } else navigate('/opportunities')
    }

    const fetchOpportunityDetailsFromCMS = async () => {
      const detailsData = await sanityServices.getOpportunityDetailsPageBySlug(
        projectSlug as string,
      )
      if (detailsData) {
        setOpportunityDetails(detailsData?.[0])
      }
    }

    if (
      (id !== 'undefined' && projectSlug !== 'undefined') ||
      (accessToken && projectSlug !== 'undefined')
    ) {
      fetchProjectById()
      fetchOpportunityDetailsFromCMS()
    } else navigate('/')
  }, [isSaveForLater, id, projectSlug, accessToken])

  return (
    <>
      {opportunity && (
        <OpportunityDetailPresenter
          project={opportunity}
          investHandleClick={investHandleClick}
          indicateInterestHandleClick={indicateInterestHandleClick}
          isIndicatingInterest={isIndicatingInterest}
          backToProjectHandler={backToProjectHandler}
          userInvestmentLatest={userInvestmentLatest}
          showContactusModal={showContactusModal}
          handleClickModal={handleClickModal}
          investButtonProperty={investButtonProperty}
          kycStatus={kycStatus}
          opportunityDetails={opportunityDetails}
        />
      )}
      {showExtendLimitModal ? (
        <ExtendLimitModal
          onClose={() => setShowExtendLimitModal(false)}
          onSubmitted={markPending}
        />
      ) : null}
      {showRequestAllocationModal && opportunity ? (
        <RequestAllocationModal
          opportunityName={opportunity.companyName || opportunity.projectName}
          minimumAmountCents={Number(opportunity.ticketSize)}
          initialAmountCents={allocationInterest?.amount}
          step={requestAllocationStep}
          submitting={isSubmittingAllocation}
          deleting={isDeletingAllocation}
          onClose={closeRequestAllocationModal}
          onSubmit={handleRequestAllocationSubmit}
          onDelete={allocationInterest ? handleRequestAllocationDelete : undefined}
        />
      ) : null}
    </>
  )
}

export { OpportunityDetailContainer }
